// Integration API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
// Note: integrationApi uses /api prefix in baseURL
const apiClient = createApiClient(`${API_BASE_URL}/api`);

export interface Integration {
  _id: string;
  userId: string;
  name: string;
  category: 'CRM' | 'Marketing' | 'Analytics' | 'Communication' | 'Social Media' | 'Other';
  status: 'connected' | 'disconnected' | 'pending' | 'error';
  config?: Record<string, any>;
  lastSync?: string;
  lastSyncStatus?: 'success' | 'failed';
  lastSyncError?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectIntegrationRequest {
  name: string;
  category: 'CRM' | 'Marketing' | 'Analytics' | 'Communication' | 'Social Media' | 'Other';
  apiKey?: string;
  apiSecret?: string;
  accessToken?: string;
  refreshToken?: string;
  config?: Record<string, any>;
  description?: string;
}

export interface UpdateIntegrationRequest {
  config?: Record<string, any>;
  description?: string;
  accessToken?: string;
  refreshToken?: string;
  apiKey?: string;
  apiSecret?: string;
}

export const getIntegrations = async (): Promise<Integration[]> => {
  try {
    const response = await apiClient.get('/integrations');
    return response.data.integrations || [];
  } catch (error: any) {
    console.error('Error fetching integrations:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch integrations');
  }
};

export const getIntegration = async (id: string): Promise<Integration> => {
  try {
    const response = await apiClient.get(`/integrations/${id}`);
    return response.data.integration;
  } catch (error: any) {
    console.error('Error fetching integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch integration');
  }
};

export const connectIntegration = async (data: ConnectIntegrationRequest): Promise<Integration> => {
  try {
    const response = await apiClient.post('/integrations/connect', data);
    return response.data.integration;
  } catch (error: any) {
    console.error('Error connecting integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to connect integration');
  }
};

export const disconnectIntegration = async (id: string): Promise<Integration> => {
  try {
    const response = await apiClient.post(`/integrations/${id}/disconnect`);
    return response.data.integration;
  } catch (error: any) {
    console.error('Error disconnecting integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to disconnect integration');
  }
};

export const updateIntegration = async (
  id: string,
  data: UpdateIntegrationRequest
): Promise<Integration> => {
  try {
    const response = await apiClient.put(`/integrations/${id}`, data);
    return response.data.integration;
  } catch (error: any) {
    console.error('Error updating integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to update integration');
  }
};

export const deleteIntegration = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/integrations/${id}`);
  } catch (error: any) {
    console.error('Error deleting integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete integration');
  }
};

export const syncIntegration = async (id: string): Promise<{ integration: Integration; data?: any; metrics?: any }> => {
  try {
    const response = await apiClient.post(`/integrations/${id}/sync`);
    return {
      integration: response.data.integration,
      data: response.data.data,
      metrics: response.data.metrics
    };
  } catch (error: any) {
    console.error('Error syncing integration:', error);
    throw new Error(error.response?.data?.message || 'Failed to sync integration');
  }
};

export const getIntegrationMetrics = async (id: string): Promise<{ latestMetrics: any; history: any[] }> => {
  try {
    const response = await apiClient.get(`/integrations/${id}/metrics`);
    return {
      latestMetrics: response.data.latestMetrics || {},
      history: response.data.history || []
    };
  } catch (error: any) {
    console.error('Error fetching integration metrics:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch integration metrics');
  }
};

// Get HubSpot OAuth authorization URL
export const getHubspotOAuthUrl = async (): Promise<{ success: boolean; authUrl: string }> => {
  try {
    const response = await apiClient.get('/integrations/hubspot/oauth-url');
    return response.data;
  } catch (error: any) {
    console.error('Error getting HubSpot OAuth URL:', error);
    throw new Error(error.response?.data?.message || 'Failed to get OAuth URL');
  }
};

export default {
  getIntegrations,
  getIntegration,
  getIntegrationMetrics,
  connectIntegration,
  disconnectIntegration,
  updateIntegration,
  deleteIntegration,
  syncIntegration,
  getHubspotOAuthUrl
};

